/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, doc, query, where, orderBy, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import { COLOR, FONT, RADIUS, SHADOW } from "./utils/theme";
import { Icon } from './utils/icons';
import useWindowWidth from "./utils/useWindowWidth";
import Toast, { useToast } from './components/Toast';
import { useAuth } from './components/AuthGate';

const T = {
  bg:        COLOR.cream,
  surface:   COLOR.white,
  primary:   COLOR.inkDark,
  secondary: COLOR.inkMid,
  accent:    COLOR.mauve,
  border:    COLOR.borderWarm,
  pillSel:   COLOR.mauveDark,
  pillUnsel: COLOR.buttonSecondary,
  cardGrad:  `linear-gradient(135deg, ${COLOR.warmSurface}, ${COLOR.white})`,
};

export default function Dashboard() {
  const { isDesktop } = useWindowWidth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingTripId, setDeletingTripId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [toast, showToast] = useToast();

  useEffect(() => {
    async function fetchTrips() {
      try {
        const snap = await getDocs(query(collection(db, "trips"), where("ownerUid", "==", user.uid), orderBy("createdAt", "desc")));
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setTrips(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchTrips();
  }, []);

  const handleDeleteTrip = async () => {
    if (!deletingTripId) return;
    setDeleteLoading(true);
    try {
      const batch = writeBatch(db);

      const actsSnap = await getDocs(
        query(collection(db, "activities"), where("ownerUid", "==", user.uid), where("tripId", "==", deletingTripId))
      );
      actsSnap.docs.forEach(d => batch.delete(doc(db, "activities", d.id)));

      const daysSnap = await getDocs(
        query(collection(db, "days"), where("ownerUid", "==", user.uid), where("tripId", "==", deletingTripId))
      );
      daysSnap.docs.forEach(d => batch.delete(doc(db, "days", d.id)));

      batch.delete(doc(db, "trips", deletingTripId));

      await batch.commit();

      setTrips(prev => prev.filter(t => t.id !== deletingTripId));
      setDeletingTripId(null);
      showToast("Trip deleted", "success");
    } catch (err) {
      console.error("[Dashboard] delete failed:", err);
      showToast("Failed to delete trip — " + err.message, "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const base = {
    minHeight: "100vh",
    background: T.bg,
    fontFamily: FONT.serif,
    color: T.primary,
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 14, opacity: 0.3, color: T.accent }}>✦</div>
          <div style={{ fontSize: 12, color: T.secondary, fontFamily: FONT.mono, letterSpacing: "0.1em" }}>
            LOADING TRIPS…
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ textAlign: "center", maxWidth: 360 }}>
          <div style={{ fontSize: 24, marginBottom: 12 }}><Icon name="warning" size={14} color={COLOR.mauveDark} /></div>
          <div style={{ fontSize: 15, color: T.accent, marginBottom: 8 }}>Failed to load trips</div>
          <div style={{ fontSize: 12, color: T.secondary, fontFamily: FONT.mono, lineHeight: 1.6 }}>{error}</div>
        </div>
      </div>
    );
  }

  // ── Dashboard ──────────────────────────────────────────────────────────────
  return (
    <div style={base}>
      {/* Header */}
      <div style={{
        padding: "0 20px",
        paddingTop: "max(18px, env(safe-area-inset-top))",
        paddingBottom: 16,
        borderBottom: `1px solid ${T.border}`,
        background: T.surface,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: `0 1px 0 ${COLOR.borderWarm}`,
      }}>
        <div>
          <div data-testid="app-title" style={{ fontSize: 32, fontWeight: 400, color: T.primary, fontFamily: FONT.display, letterSpacing: "0.05em", lineHeight: 1 }}>
            Wanderologie
          </div>
          <div style={{ fontSize: 11, color: T.secondary, fontFamily: FONT.mono, letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 4 }}>
            every detail, perfectly placed
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={signOut}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: COLOR.inkMid,
              fontSize: isDesktop ? 12 : 13,
              fontFamily: FONT.mono,
              letterSpacing: "0.03em",
              padding: "4px 6px",
              textDecoration: "none",
            }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
            onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
          >
            Sign out
          </button>
          {isDesktop && (
            <button
              data-testid="create-trip-button"
              onClick={() => navigate('/trip/new')}
              style={{
                background: T.accent,
                border: "none",
                borderRadius: 12,
                color: COLOR.white,
                padding: "0 18px",
                height: 44,
                cursor: "pointer",
                fontSize: 13,
                fontFamily: FONT.mono,
                letterSpacing: "0.04em",
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              Plan a Trip ✦
            </button>
          )}
          {/* TODO: move settings to user menu */}
          {isDesktop && user.uid === "ee88W1zGUSNrBxdpAgG1BaH0xjn2" && (
            <button
              onClick={() => navigate('/settings')}
              title="Settings"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: COLOR.inkMid,
                fontSize: 18,
                padding: "4px 8px",
              }}
            >
              <Icon name="settings" size={16} color={COLOR.inkMid} />
            </button>
          )}
        </div>
      </div>

      {/* Trip list */}
      <div data-testid="trip-list" style={{ padding: isDesktop ? "20px 16px" : "20px 12px", display: "flex", flexDirection: "column", gap: 16 }}>
        {trips.length === 0 ? (
          <EmptyState onPlan={() => navigate('/trip/new')} />
        ) : (
          trips.map((trip) => (
            <TripCard key={trip.id} trip={trip} onClick={() => navigate(`/trip/${trip.id}`)} onDelete={() => setDeletingTripId(trip.id)} />
          ))
        )}
      </div>

      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      {/* Mobile FAB */}
      {!isDesktop && (
        <button
          data-testid="create-trip-button"
          onClick={() => navigate('/trip/new')}
          style={{
            position: "fixed",
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
            background: COLOR.mauve,
            color: COLOR.white,
            border: "none",
            borderRadius: RADIUS.pill,
            padding: "14px 24px",
            fontSize: 15,
            fontWeight: 600,
            fontFamily: FONT.serif,
            cursor: "pointer",
            boxShadow: SHADOW.panel,
          }}
        >
          Plan a Trip ✦
        </button>
      )}

      {/* Delete confirmation modal */}
      {deletingTripId && (
        <div
          onClick={() => { if (!deleteLoading) setDeletingTripId(null); }}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: COLOR.white, borderRadius: 16,
              padding: 32, maxWidth: 400, width: "100%",
              boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{
              fontSize: 18, fontWeight: 600, color: T.primary,
              fontFamily: FONT.serif, marginBottom: 12,
            }}>
              Delete this trip?
            </div>
            <div style={{
              fontSize: 13, color: T.secondary, lineHeight: 1.6,
              fontFamily: FONT.serif, marginBottom: 24,
            }}>
              This will permanently delete the trip, all days, and all activities. This cannot be undone.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setDeletingTripId(null)}
                disabled={deleteLoading}
                style={{
                  background: "none", border: `1px solid ${T.border}`,
                  borderRadius: 10, color: T.secondary,
                  padding: "10px 20px", cursor: deleteLoading ? "default" : "pointer",
                  fontFamily: FONT.mono, fontSize: 12,
                  letterSpacing: "0.04em", opacity: deleteLoading ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTrip}
                disabled={deleteLoading}
                style={{
                  background: COLOR.mauveDark, border: "none",
                  borderRadius: 10, color: COLOR.white,
                  padding: "10px 20px", cursor: deleteLoading ? "default" : "pointer",
                  fontFamily: FONT.mono, fontSize: 12,
                  letterSpacing: "0.04em", opacity: deleteLoading ? 0.7 : 1,
                }}
              >
                {deleteLoading ? "Deleting…" : "Delete trip"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const HEADLINES = [
  "Start planning something beautiful.",
  "Your next trip starts here.",
  "Every detail. Every day. Sorted.",
];
const SUBTEXTS = [
  "Research, organize, and perfect every detail — then share the whole thing with your travel crew.",
  "Your trips deserve better than a Notes app screenshot and good intentions.",
  "Build a day-by-day itinerary you'll actually want to share. Every restaurant, every museum, every golden hour walk.",
  "The planning is the fun part. Curate your perfect trip — every day, every stop, every vibe — and send it to the group chat.",
  "Your itinerary, your research, your vision. Organized by day, shareable in one link, and gorgeous enough to screenshot.",
];

function EmptyState({ onPlan }) {
  const { isDesktop } = useWindowWidth();
  const headline = useMemo(() => HEADLINES[Math.floor(Math.random() * HEADLINES.length)], []);
  const subtext = useMemo(() => SUBTEXTS[Math.floor(Math.random() * SUBTEXTS.length)], []);

  return (
    <div data-testid="empty-state" style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: isDesktop ? "60px 20px" : "60px 24px", textAlign: "center",
    }}>
      <div style={{
        fontFamily: FONT.display, fontSize: 28, fontWeight: 300,
        color: COLOR.inkDark, textAlign: "center", marginBottom: 12,
      }}>
        {headline}
      </div>
      <div style={{
        fontFamily: FONT.mono, fontSize: 14, color: COLOR.inkMid,
        textAlign: "center", maxWidth: 360, lineHeight: 1.6, marginBottom: 32,
      }}>
        {subtext}
      </div>
      <button
        onClick={onPlan}
        style={{
          background: T.accent, border: "none", borderRadius: 12,
          color: COLOR.white, padding: "14px 32px", cursor: "pointer",
          fontSize: 16, fontFamily: FONT.mono, letterSpacing: "0.04em",
          ...(!isDesktop ? { width: "100%", maxWidth: 320 } : {}),
        }}
      >
        Plan a Trip ✦
      </button>
    </div>
  );
}

function getStatusBadgeText(trip) {
  if (trip.status === "wave1_complete" || trip.status === "wave2_complete") return "Building your trip…";
  if (trip.status === "creating" && !trip.wave3Started) return "Building your trip…";
  if (trip.status === "creating" && trip.wave3Started) return "Generating activities…";
  return null;
}

function TripCard({ trip, onClick, onDelete }) {
  const { isDesktop } = useWindowWidth();
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Fallback chains: top-level → trip.brief
  const startRaw    = trip.startDate || trip.brief?.startDate || null;
  const endRaw      = trip.endDate   || trip.brief?.endDate   || null;
  const start       = startRaw ? formatDate(startRaw) : null;
  const end         = endRaw   ? formatDate(endRaw)   : null;
  const hasDates    = !!(start || end);
  const countdown   = startRaw ? getCountdown(startRaw) : null;

  const travelersRaw = (Array.isArray(trip.brief?.travelerNames) && trip.brief.travelerNames.length > 0 ? trip.brief.travelerNames : null)
    || trip.participants || trip.brief?.travelers || null;
  const travelers = Array.isArray(travelersRaw)
    ? (travelersRaw.length > 0 ? travelersRaw.join(" & ") : null)
    : typeof travelersRaw === "number"
      ? `${travelersRaw} travelers`
      : travelersRaw || null;

  // City pills: cities → routing → brief.destinations → brief.destination as single pill
  const routing = Array.isArray(trip.cities) && trip.cities.length > 0
    ? trip.cities
    : Array.isArray(trip.routing) && trip.routing.length > 0
      ? trip.routing
      : Array.isArray(trip.brief?.destinations) && trip.brief.destinations.length > 0
        ? trip.brief.destinations
        : trip.brief?.destination
          ? [trip.brief.destination]
          : [];
  const heroUrl = trip.heroImg || trip.heroImage || null;
  const hasPhoto = !!heroUrl;
  const badgeText = getStatusBadgeText(trip);
  const hasBadge = !!badgeText;

  return (
    <div
      data-testid={`trip-card-${trip.slug || trip.id}`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      style={{
        position: "relative",
        borderRadius: 16,
        overflow: "hidden",
        cursor: "pointer",
        border: hovered
          ? `1px solid ${T.accent}`
          : `1px solid ${T.border}`,
        background: hasPhoto
          ? `url(${heroUrl}) center/cover no-repeat`
          : T.surface,
        boxShadow: hovered
          ? "0 4px 20px rgba(176,112,128,0.15)"
          : "0 2px 12px rgba(44,24,16,0.08)",
        transform: pressed ? "scale(0.98)" : "scale(1)",
        opacity: pressed ? 0.88 : 1,
        transition: "transform 0.12s, opacity 0.12s, border-color 0.2s, box-shadow 0.2s",
        WebkitTapHighlightColor: "transparent",
        minHeight: hasPhoto ? 220 : "auto",
      }}
    >
      {/* Gradient overlay */}
      <div data-testid={`trip-hero-${trip.slug || trip.id}`} style={{
        position: "absolute",
        inset: 0,
        background: hasPhoto
          ? `linear-gradient(to bottom, transparent 30%, ${COLOR.overlayDark} 100%)`
          : hovered
            ? `linear-gradient(135deg, ${COLOR.blush}, ${COLOR.white})`
            : T.cardGrad,
        transition: "background 0.2s",
      }} />

      {/* Delete button */}
      <button
        onClick={e => { e.stopPropagation(); onDelete(); }}
        style={{
          position: "absolute", top: 8, right: 8, zIndex: 2,
          background: hasPhoto ? "rgba(0,0,0,0.35)" : COLOR.buttonSecondary,
          border: "none", borderRadius: 8,
          width: 44, height: 44,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          transition: "background 0.15s",
        }}
      >
        <Icon name="delete" size={16} color={hasPhoto ? "rgba(255,255,255,0.8)" : COLOR.inkLight} />
      </button>

      {/* Content */}
      <div style={{
        position: "relative",
        zIndex: 1,
        padding: hasPhoto ? "120px 20px 20px" : "20px 20px 18px",
      }}>
        {/* Title row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <div style={{
            fontSize: 22,
            fontWeight: 400,
            color: hasPhoto ? COLOR.white : T.primary,
            lineHeight: 1.2,
            flex: 1,
            paddingRight: 32,
            minWidth: 0,
          }}>
            {!isDesktop ? (
              <span style={{ display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{trip.title || trip.id}</span>
            ) : (trip.title || trip.id)}
          </div>
          <span style={{
            color: hasPhoto ? "rgba(255,255,255,0.8)" : T.accent,
            fontSize: 22,
            opacity: hovered ? 1 : 0.5,
            flexShrink: 0,
            marginTop: 1,
            transition: "opacity 0.2s",
          }}>›</span>
        </div>

        {/* Routing pills + status badge row */}
        {(routing.length > 0 || hasBadge) && (
          <div data-testid={`trip-cities-${trip.slug || trip.id}`} style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
            {routing.map((city, i) => (
              <span key={i} style={{
                fontSize: 12,
                fontFamily: FONT.mono,
                color: hasPhoto ? "rgba(255,255,255,0.9)" : T.accent,
                background: hasPhoto ? "rgba(255,255,255,0.15)" : `${T.accent}18`,
                border: `1px solid ${hasPhoto ? "rgba(255,255,255,0.25)" : T.accent + "33"}`,
                borderRadius: 8,
                padding: "4px 10px",
                letterSpacing: "0.03em",
              }}>
                {city}
              </span>
            ))}
            {hasBadge && (
              <span style={{
                marginLeft: "auto",
                background: `rgba(${parseInt(COLOR.mauve.slice(1,3),16)},${parseInt(COLOR.mauve.slice(3,5),16)},${parseInt(COLOR.mauve.slice(5,7),16)},0.85)`,
                color: COLOR.white, fontSize: 11, fontFamily: FONT.mono,
                padding: "4px 10px", borderRadius: 12,
                whiteSpace: "nowrap",
              }}>
                {badgeText}
              </span>
            )}
          </div>
        )}

        {/* Meta row */}
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end" }}>
          {hasDates && (
            <div>
              <div style={{ fontSize: 10, color: hasPhoto ? "rgba(255,255,255,0.55)" : T.secondary, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 3 }}>DATES</div>
              <div style={{ fontSize: 12, color: hasPhoto ? "rgba(255,255,255,0.85)" : T.secondary, fontFamily: FONT.mono }}>{start || "—"} → {end || "—"}</div>
            </div>
          )}
          {travelers && (
            <div data-testid={`trip-travelers-${trip.slug || trip.id}`}>
              <div style={{ fontSize: 10, color: hasPhoto ? "rgba(255,255,255,0.55)" : T.secondary, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 3 }}>TRAVELERS</div>
              <div style={{ fontSize: 12, color: hasPhoto ? "rgba(255,255,255,0.85)" : T.secondary, fontFamily: FONT.mono }}>{travelers}</div>
            </div>
          )}
          {countdown && (
            <div style={{ marginLeft: "auto" }}>
              <div style={{
                fontSize: 11,
                fontFamily: FONT.mono,
                color: countdown.past
                  ? (hasPhoto ? "rgba(255,255,255,0.6)" : T.secondary)
                  : (hasPhoto ? COLOR.mauveLight : T.accent),
                background: countdown.past
                  ? (hasPhoto ? "rgba(255,255,255,0.1)" : T.bg)
                  : (hasPhoto ? "rgba(176,112,128,0.35)" : `${T.accent}18`),
                border: `1px solid ${countdown.past
                  ? (hasPhoto ? "rgba(255,255,255,0.15)" : T.border)
                  : (hasPhoto ? "rgba(176,112,128,0.4)" : T.accent + "44")}`,
                borderRadius: 8,
                padding: "4px 10px",
                letterSpacing: "0.03em",
                whiteSpace: "nowrap",
              }}>
                {countdown.label}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00Z");
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

function getCountdown(iso) {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(iso + "T00:00:00Z");
    if (isNaN(target)) return null;
    const diffDays = Math.round((target - today) / 86400000);
    if (diffDays === 0)  return { label: "Today!",     past: false };
    if (diffDays === 1)  return { label: "Tomorrow!",  past: false };
    if (diffDays === -1) return { label: "Yesterday",  past: true  };
    if (diffDays > 0)    return { label: `${diffDays} days away`, past: false };
    return { label: `${Math.abs(diffDays)} days ago`, past: true };
  } catch {
    return null;
  }
}
